/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License. See License.txt in the project root for
 * license information.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is
 * regenerated.
 */
import { __assign } from "tslib";
import * as msRest from "@azure/ms-rest-js";
import * as Mappers from "../models/labsMappers";
import * as Parameters from "../models/parameters";
/** Class representing a Labs. */
var Labs = /** @class */function () {
  /**
   * Create a Labs.
   * @param {LabServicesClientContext} client Reference to the service client.
   */
  function Labs(client) {
    this.client = client;
  }
  Labs.prototype.listBySubscription = function (options, callback) {
    return this.client.sendOperationRequest({
      options: options
    }, listBySubscriptionOperationSpec, callback);
  };
  Labs.prototype.listByResourceGroup = function (resourceGroupName, options, callback) {
    return this.client.sendOperationRequest({
      resourceGroupName: resourceGroupName,
      options: options
    }, listByResourceGroupOperationSpec, callback);
  };
  Labs.prototype.get = function (resourceGroupName, labName, options, callback) {
    return this.client.sendOperationRequest({
      resourceGroupName: resourceGroupName,
      labName: labName,
      options: options
    }, getOperationSpec, callback);
  };
  /**
   * Operation to create or update a lab resource.
   * @summary Create or update a lab resource.
   * @param body The request body.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param labName The name of the lab that uniquely identifies it within containing lab plan. Used
   * in resource URIs.
   * @param [options] The optional parameters
   * @returns Promise<Models.LabsCreateOrUpdateResponse>
   */
  Labs.prototype.createOrUpdate = function (body, resourceGroupName, labName, options) {
    return this.beginCreateOrUpdate(body, resourceGroupName, labName, options).then(function (lroPoller) {
      return lroPoller.pollUntilFinished();
    });
  };
  /**
   * Operation to update a lab resource.
   * @summary Update a lab resource.
   * @param body The request body.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param labName The name of the lab that uniquely identifies it within containing lab plan. Used
   * in resource URIs.
   * @param [options] The optional parameters
   * @returns Promise<Models.LabsUpdateResponse>
   */
  Labs.prototype.update = function (body, resourceGroupName, labName, options) {
    return this.beginUpdate(body, resourceGroupName, labName, options).then(function (lroPoller) {
      return lroPoller.pollUntilFinished();
    });
  };
  /**
   * Operation to delete a lab resource.
   * @summary Deletes a lab resource.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param labName The name of the lab that uniquely identifies it within containing lab plan. Used
   * in resource URIs.
   * @param [options] The optional parameters
   * @returns Promise<msRest.RestResponse>
   */
  Labs.prototype.deleteMethod = function (resourceGroupName, labName, options) {
    return this.beginDeleteMethod(resourceGroupName, labName, options).then(function (lroPoller) {
      return lroPoller.pollUntilFinished();
    });
  };
  /**
   * Publish or re-publish a lab. This will create or update all lab resources, such as virtual
   * machines.
   * @summary Publish or re-publish a lab.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param labName The name of the lab that uniquely identifies it within containing lab plan. Used
   * in resource URIs.
   * @param [options] The optional parameters
   * @returns Promise<msRest.RestResponse>
   */
  Labs.prototype.publish = function (resourceGroupName, labName, options) {
    return this.beginPublish(resourceGroupName, labName, options).then(function (lroPoller) {
      return lroPoller.pollUntilFinished();
    });
  };
  /**
   * Action used to manually kick off an AAD group sync job.
   * @summary Manually sync the lab group.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param labName The name of the lab that uniquely identifies it within containing lab plan. Used
   * in resource URIs.
   * @param [options] The optional parameters
   * @returns Promise<msRest.RestResponse>
   */
  Labs.prototype.syncGroup = function (resourceGroupName, labName, options) {
    return this.beginSyncGroup(resourceGroupName, labName, options).then(function (lroPoller) {
      return lroPoller.pollUntilFinished();
    });
  };
  /**
   * Operation to create or update a lab resource.
   * @summary Create or update a lab resource.
   * @param body The request body.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param labName The name of the lab that uniquely identifies it within containing lab plan. Used
   * in resource URIs.
   * @param [options] The optional parameters
   * @returns Promise<msRestAzure.LROPoller>
   */
  Labs.prototype.beginCreateOrUpdate = function (body, resourceGroupName, labName, options) {
    return this.client.sendLRORequest({
      body: body,
      resourceGroupName: resourceGroupName,
      labName: labName,
      options: options
    }, beginCreateOrUpdateOperationSpec, options);
  };
  /**
   * Operation to update a lab resource.
   * @summary Update a lab resource.
   * @param body The request body.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param labName The name of the lab that uniquely identifies it within containing lab plan. Used
   * in resource URIs.
   * @param [options] The optional parameters
   * @returns Promise<msRestAzure.LROPoller>
   */
  Labs.prototype.beginUpdate = function (body, resourceGroupName, labName, options) {
    return this.client.sendLRORequest({
      body: body,
      resourceGroupName: resourceGroupName,
      labName: labName,
      options: options
    }, beginUpdateOperationSpec, options);
  };
  /**
   * Operation to delete a lab resource.
   * @summary Deletes a lab resource.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param labName The name of the lab that uniquely identifies it within containing lab plan. Used
   * in resource URIs.
   * @param [options] The optional parameters
   * @returns Promise<msRestAzure.LROPoller>
   */
  Labs.prototype.beginDeleteMethod = function (resourceGroupName, labName, options) {
    return this.client.sendLRORequest({
      resourceGroupName: resourceGroupName,
      labName: labName,
      options: options
    }, beginDeleteMethodOperationSpec, options);
  };
  /**
   * Publish or re-publish a lab. This will create or update all lab resources, such as virtual
   * machines.
   * @summary Publish or re-publish a lab.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param labName The name of the lab that uniquely identifies it within containing lab plan. Used
   * in resource URIs.
   * @param [options] The optional parameters
   * @returns Promise<msRestAzure.LROPoller>
   */
  Labs.prototype.beginPublish = function (resourceGroupName, labName, options) {
    return this.client.sendLRORequest({
      resourceGroupName: resourceGroupName,
      labName: labName,
      options: options
    }, beginPublishOperationSpec, options);
  };
  /**
   * Action used to manually kick off an AAD group sync job.
   * @summary Manually sync the lab group.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param labName The name of the lab that uniquely identifies it within containing lab plan. Used
   * in resource URIs.
   * @param [options] The optional parameters
   * @returns Promise<msRestAzure.LROPoller>
   */
  Labs.prototype.beginSyncGroup = function (resourceGroupName, labName, options) {
    return this.client.sendLRORequest({
      resourceGroupName: resourceGroupName,
      labName: labName,
      options: options
    }, beginSyncGroupOperationSpec, options);
  };
  Labs.prototype.listBySubscriptionNext = function (nextPageLink, options, callback) {
    return this.client.sendOperationRequest({
      nextPageLink: nextPageLink,
      options: options
    }, listBySubscriptionNextOperationSpec, callback);
  };
  Labs.prototype.listByResourceGroupNext = function (nextPageLink, options, callback) {
    return this.client.sendOperationRequest({
      nextPageLink: nextPageLink,
      options: options
    }, listByResourceGroupNextOperationSpec, callback);
  };
  return Labs;
}();
export { Labs };
// Operation Specifications
var serializer = new msRest.Serializer(Mappers);
var listBySubscriptionOperationSpec = {
  httpMethod: "GET",
  path: "subscriptions/{subscriptionId}/providers/Microsoft.LabServices/labs",
  urlParameters: [Parameters.subscriptionId],
  queryParameters: [Parameters.apiVersion, Parameters.filter],
  headerParameters: [Parameters.acceptLanguage],
  responses: {
    200: {
      bodyMapper: Mappers.PagedLabs
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  serializer: serializer
};
var listByResourceGroupOperationSpec = {
  httpMethod: "GET",
  path: "subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.LabServices/labs",
  urlParameters: [Parameters.subscriptionId, Parameters.resourceGroupName],
  queryParameters: [Parameters.apiVersion],
  headerParameters: [Parameters.acceptLanguage],
  responses: {
    200: {
      bodyMapper: Mappers.PagedLabs
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  serializer: serializer
};
var getOperationSpec = {
  httpMethod: "GET",
  path: "subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.LabServices/labs/{labName}",
  urlParameters: [Parameters.subscriptionId, Parameters.resourceGroupName, Parameters.labName],
  queryParameters: [Parameters.apiVersion],
  headerParameters: [Parameters.acceptLanguage],
  responses: {
    200: {
      bodyMapper: Mappers.Lab
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  serializer: serializer
};
var beginCreateOrUpdateOperationSpec = {
  httpMethod: "PUT",
  path: "subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.LabServices/labs/{labName}",
  urlParameters: [Parameters.subscriptionId, Parameters.resourceGroupName, Parameters.labName],
  queryParameters: [Parameters.apiVersion],
  headerParameters: [Parameters.acceptLanguage],
  requestBody: {
    parameterPath: "body",
    mapper: __assign(__assign({}, Mappers.Lab), {
      required: true
    })
  },
  responses: {
    200: {
      bodyMapper: Mappers.Lab
    },
    201: {
      bodyMapper: Mappers.Lab
    },
    202: {
      bodyMapper: Mappers.Lab
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  serializer: serializer
};
var beginUpdateOperationSpec = {
  httpMethod: "PATCH",
  path: "subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.LabServices/labs/{labName}",
  urlParameters: [Parameters.subscriptionId, Parameters.resourceGroupName, Parameters.labName],
  queryParameters: [Parameters.apiVersion],
  headerParameters: [Parameters.acceptLanguage],
  requestBody: {
    parameterPath: "body",
    mapper: __assign(__assign({}, Mappers.LabUpdate), {
      required: true
    })
  },
  responses: {
    200: {
      bodyMapper: Mappers.Lab
    },
    202: {
      bodyMapper: Mappers.Lab
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  serializer: serializer
};
var beginDeleteMethodOperationSpec = {
  httpMethod: "DELETE",
  path: "subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.LabServices/labs/{labName}",
  urlParameters: [Parameters.subscriptionId, Parameters.resourceGroupName, Parameters.labName],
  queryParameters: [Parameters.apiVersion],
  headerParameters: [Parameters.acceptLanguage],
  responses: {
    200: {},
    202: {},
    204: {},
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  serializer: serializer
};
var beginPublishOperationSpec = {
  httpMethod: "POST",
  path: "subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.LabServices/labs/{labName}/publish",
  urlParameters: [Parameters.subscriptionId, Parameters.resourceGroupName, Parameters.labName],
  queryParameters: [Parameters.apiVersion],
  headerParameters: [Parameters.acceptLanguage],
  responses: {
    200: {},
    202: {},
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  serializer: serializer
};
var beginSyncGroupOperationSpec = {
  httpMethod: "POST",
  path: "subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.LabServices/labs/{labName}/syncGroup",
  urlParameters: [Parameters.subscriptionId, Parameters.resourceGroupName, Parameters.labName],
  queryParameters: [Parameters.apiVersion],
  headerParameters: [Parameters.acceptLanguage],
  responses: {
    200: {},
    202: {},
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  serializer: serializer
};
var listBySubscriptionNextOperationSpec = {
  httpMethod: "GET",
  baseUrl: "https://management.azure.com",
  path: "{nextLink}",
  urlParameters: [Parameters.nextPageLink],
  headerParameters: [Parameters.acceptLanguage],
  responses: {
    200: {
      bodyMapper: Mappers.PagedLabs
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  serializer: serializer
};
var listByResourceGroupNextOperationSpec = {
  httpMethod: "GET",
  baseUrl: "https://management.azure.com",
  path: "{nextLink}",
  urlParameters: [Parameters.nextPageLink],
  headerParameters: [Parameters.acceptLanguage],
  responses: {
    200: {
      bodyMapper: Mappers.PagedLabs
    },
    default: {
      bodyMapper: Mappers.CloudError
    }
  },
  serializer: serializer
};